import { useModal } from '@billy/design-system'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AutomaticPaymentRemindersType,
  type PaymentRemindersDetails,
  useGetAutomaticPaymentRemindersSettings,
} from '@features/invoicingSettings/modules/paymentsAndReminders'

import { ModalId } from '../../../../../../../enums/modalId'

export interface UseInvoiceSettingsModalsAutomaticPaymentRemindersResult {
  defaultValues?: PaymentRemindersDetails
  isLoading: boolean
  onSubmit: (paymentRemindersDetails: PaymentRemindersDetails) => void
  openModal: (variant: AutomaticPaymentRemindersType, formPaymentRemindersDetails: PaymentRemindersDetails) => void
  paymentRemindersDetails?: PaymentRemindersDetails
  shouldUpdateForm: boolean
  variant: AutomaticPaymentRemindersType
}

export const useInvoiceSettingsModalsAutomaticPaymentReminders =
  (): UseInvoiceSettingsModalsAutomaticPaymentRemindersResult => {
    const [shouldUpdateForm, setShouldUpdateForm] = useState<boolean>(false)
    const [paymentRemindersDetails, setPaymentRemindersDetails] = useState<PaymentRemindersDetails | undefined>()
    const [variant, setVariant] = useState(AutomaticPaymentRemindersType.AfterDueDate)
    const { t } = useTranslation()
    const { open: openAutomaticPaymentRemindersModal, close: closeAutomaticPaymentRemindersModal } = useModal(
      ModalId.AutomaticPaymentRemindersModal,
    )
    const { automaticPaymentRemindersSettings, isLoading } = useGetAutomaticPaymentRemindersSettings(t)

    const handleSubmit = useCallback(
      (updatedPaymentRemindersDetails: PaymentRemindersDetails) => {
        setPaymentRemindersDetails(updatedPaymentRemindersDetails)
        setShouldUpdateForm(true)
        closeAutomaticPaymentRemindersModal()
      },
      [closeAutomaticPaymentRemindersModal],
    )

    const handleOpen = useCallback(
      (modalVariant: AutomaticPaymentRemindersType, formPaymentRemindersDetails: PaymentRemindersDetails) => {
        setVariant(modalVariant)
        setPaymentRemindersDetails(formPaymentRemindersDetails)
        setShouldUpdateForm(false)
        openAutomaticPaymentRemindersModal()
      },
      [openAutomaticPaymentRemindersModal],
    )

    return {
      defaultValues: paymentRemindersDetails ?? automaticPaymentRemindersSettings?.[variant],
      isLoading,
      openModal: handleOpen,
      onSubmit: handleSubmit,
      paymentRemindersDetails,
      shouldUpdateForm,
      variant,
    }
  }
